import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, KeyRound, ShieldCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AdminSetupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [token, setToken] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API_URL}/api/admin/auth-status`)
      .then((r) => r.json())
      .then((d) => { if (d.setup_complete) setLocked(true); })
      .catch(() => {});
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, setup_token: token }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        navigate('/admin');
      } else if (res.status === 403) {
        setError(data.detail || 'Setup is not available.');
      } else {
        setError(data.detail || 'Could not complete setup. Please try again.');
      }
    } catch {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0] flex items-center justify-center px-4">
      <Helmet>
        <title>Admin Setup | TCUBE FITNESS™</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E88E5] rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1F2933]">First-Run Admin Setup</h1>
          <p className="text-[#64748B] mt-2">Create your administrator account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {locked ? (
            <div className="text-center" data-testid="setup-locked">
              <p className="text-[#1F2933] font-semibold mb-2">Setup already completed</p>
              <p className="text-[#64748B] text-sm mb-4">An administrator account already exists.</p>
              <button onClick={() => navigate('/admin')} className="text-[#1E88E5] font-semibold hover:underline">
                Go to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="admin-setup-form">
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1F2933] mb-2">Admin Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                  <input type="email" data-testid="setup-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] outline-none" placeholder="you@example.com" />
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1F2933] mb-2">Password (min 8 chars)</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                  <input type={showPw ? 'text' : 'password'} data-testid="setup-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="w-full pl-10 pr-12 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] outline-none" placeholder="Create a strong password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPw ? <EyeOff className="h-5 w-5 text-[#94A3B8]" /> : <Eye className="h-5 w-5 text-[#94A3B8]" />}
                  </button>
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-[#1F2933] mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                  <input type={showPw ? 'text' : 'password'} data-testid="setup-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] outline-none" placeholder="Re-enter your password" />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1F2933] mb-2">Setup Token</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                  <input type="password" data-testid="setup-token-input" value={token} onChange={(e) => setToken(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#1E88E5] outline-none" placeholder="One-time setup token" />
                </div>
                <p className="text-xs text-[#94A3B8] mt-1.5">Provided securely by your developer. Used only once.</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="setup-error">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="setup-submit-btn"
                className="w-full py-3 px-4 bg-[#1E88E5] text-white font-semibold rounded-xl hover:bg-[#1976D2] transition-colors disabled:opacity-50">
                {loading ? 'Creating account...' : 'Create Administrator'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSetupPage;
